import React, {useState, useEffect} from 'react'
import {Controller} from 'react-hook-form'
import classNames from 'classnames'
import Proptypes from "prop-types"
//************************************//
import {trans} from '@utils'
//************************************//
import UncontrolledDropdownTree from "./UncontrolledDropdownTree"
//************************************//
const Component = React.forwardRef(({ name, value, valueType, outputType, className, onChange, onInputChange, onClose, remoteUrl, defaultOptions, loadOptions, multi, disabled, group, clearable, groupOptionFormat, optionFormatter, dropdownOptions, dropDownComponents }, ref)  => {

  useEffect(() => {
    onChange(value)
  }, [value])

  return (
    <UncontrolledDropdownTree
      name={name}
      value={value}
      valueType={valueType}
      onChange={({ value, options }) => {
        if (outputType === 'array') onChange(value)
        if (outputType === 'arrayObject') onChange(options)
        if (outputType === 'default') onChange(group ? value : options) // just for now
        onInputChange({ value, options })
      }}
      onClose={onClose}
      className={className}
      remoteUrl={remoteUrl}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      optionFormatter={optionFormatter}
      dropdownOptions={dropdownOptions}
      dropDownComponents={dropDownComponents}
      multi={multi}
      disabled={disabled}
      group={group}
      clearable={clearable}
      groupOptionFormat={groupOptionFormat}
    />
  )
})
const DropdownTree = ({
  name = '',
  control = null,
  className = '',
  defaultValue = [],
  valueType = 'array',
  outputType = 'default',
  onChange = () => {},
  onClose = () => {},
  remoteUrl = '',
  defaultOptions = null,
  loadOptions = () => {},
  multi = true,
  disabled = false,
  group = false,
  clearable = false,
  groupOptionFormat = null,
  optionFormatter = (option) => ({
    ...option,
    groupCode: option.groupCode,
    groupLabel: option.groupLabel,
    value: option.value,
    label: option.label,
    hasChildren: option.children?.length > 0,
    ancestors: option.ancestors,
    fixed: option.fixed,
    canSelect: true
  }),
  dropdownOptions = {
    dropdownDirection: 'down',
    fixedPosition: false,
    dropdownClassName: '',
    dropdownStyle: {},
    dropdownMenuFlip: true,
    dropdownMenuHeight: '250px',
    showDropdownLeafIcon: true,
    dropdownLeafCustomIcon: null,
    addNewAction: { enable: false, title: 'Add new', className: '', onClick: () => {} }
  },
  dropDownComponents = { dropdownItemConetnt: null },
  rules = { required: false, validate: {} }
}) => {
  const {required, validate, ...restRules} = rules
  const rulesValidation = {}
  if (required) {
    rulesValidation.validate = {
      valid: (value) => {
        return value?.length > 0 || trans('gen.validations.required')
      },
      ...validate
    }
  } else {
    rulesValidation.validate = validate
  }
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      valueType={valueType}
      outputType={outputType}
      className={classNames(className, { "is-invalid": required })}
      as={Component}
      onInputChange={onChange}
      onClose={onClose}
      remoteUrl={remoteUrl}
      defaultOptions={defaultOptions}
      loadOptions={loadOptions}
      optionFormatter={optionFormatter}
      dropdownOptions={dropdownOptions}
      dropDownComponents={dropDownComponents}
      multi={multi}
      disabled={disabled}
      group={group}
      clearable={clearable}
      groupOptionFormat={groupOptionFormat}
      rules={{
        ...rulesValidation,
        ...restRules
      }}
    />
  )
}
//************************************//
DropdownTree.propTypes = {
  name: Proptypes.string.isRequired,
  control: Proptypes.any,
  defaultValue: Proptypes.oneOfType([
    Proptypes.arrayOf(Proptypes.any),
    Proptypes.arrayOf(
      Proptypes.shape({
        value: Proptypes.any
      })
    )
  ]),
  valueType: Proptypes.oneOf(['array', 'arrayObject']),
  outputType: Proptypes.oneOf(['default', 'array', 'arrayObject']),
  className: Proptypes.string,
  onChange: Proptypes.func,
  onClose: Proptypes.func,
  remoteUrl: Proptypes.string,
  defaultOptions: Proptypes.arrayOf(Proptypes.any),
  loadOptions: Proptypes.func,
  delaySearch: Proptypes.number,
  optionFormatter: Proptypes.func,
  dropdownOptions: Proptypes.shape({
    dropdownDirection: Proptypes.oneOf(['up', 'down']),
    fixedPosition: Proptypes.bool,
    dropdownClassName: Proptypes.string,
    dropdownStyle: Proptypes.object,
    dropdownMenuFlip: Proptypes.bool,
    dropdownMenuHeight: Proptypes.string,
    showDropdownLeafIcon: Proptypes.bool,
    dropdownLeafCustomIcon: Proptypes.any,
    addNewAction: Proptypes.shape({
      enable: Proptypes.bool,
      title: Proptypes.string,
      className: Proptypes.string,
      onClick: Proptypes.func
    })
  }),
  dropDownComponents: Proptypes.shape({
    dropdownItemConetnt: Proptypes.func
  }),
  rules: Proptypes.shape({
    required: Proptypes.bool,
    validate: Proptypes.object
  }),
  multi: Proptypes.bool,
  disabled: Proptypes.bool,
  group: Proptypes.bool,
  clearable: Proptypes.bool,
  groupOptionFormat: Proptypes.objectOf(
    Proptypes.shape({
      optionsMap: Proptypes.shape({
        value: Proptypes.string,
        label: Proptypes.string,
        fixed: Proptypes.string
      }),
      groupCode: Proptypes.string
    })
  )
}
//************************************//
export default DropdownTree
